/**
 * Events Storefront Loader
 * Fetches all event metaobjects, sorts newest-first, and renders them into the listing.
 *
 * This mirrors the Authors Storefront loader pattern.
 */

(() => {
  const grid = document.querySelector("[data-events-grid]");
  if (!grid) return;

  const loader = grid.querySelector("[data-events-loading]");
  const errorMessage = grid.querySelector("[data-events-error]");
  const emptyState = grid.querySelector("[data-events-empty]");
  const loadMoreWrapper = grid.querySelector("[data-events-load-more]");
  const loadMoreButton = grid.querySelector("[data-events-load-more-button]");

  const token = grid.dataset.storefrontToken;
  const apiVersion = grid.dataset.storefrontApiVersion || "2024-10";

  const perPageRaw = grid.dataset.eventsPerPage || "12";
  const pageSize =
    perPageRaw === "all" ? Infinity : Math.max(1, Number(perPageRaw) || 12);

  const showPastEvents =
    String(grid.dataset.showPastEvents || "true") === "true";

  if (!token) return;

  const endpoint = `${window.location.origin}/api/${apiVersion}/graphql.json`;

  const escapeHtml = (value) => {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  };

  const truncate = (value, max) => {
    const text = String(value ?? "").trim();
    if (!text) return "";
    if (text.length <= max) return text;
    return `${text.slice(0, Math.max(0, max - 1))}…`;
  };

  const richTextJsonToPlainText = (root) => {
    const parts = [];

    const walk = (node) => {
      if (!node) return;

      if (Array.isArray(node)) {
        for (const child of node) walk(child);
        return;
      }

      if (typeof node === "string") {
        parts.push(node);
        return;
      }

      if (typeof node !== "object") return;

      // Shopify rich text commonly uses { type, children } with text nodes.
      if (typeof node.text === "string") parts.push(node.text);

      // Storefront API rich text returns text nodes as: { type: 'text', value: '...' }
      if (typeof node.value === "string") parts.push(node.value);

      if (Array.isArray(node.children)) {
        for (const child of node.children) walk(child);

        // Add a soft block separator for common block nodes.
        if (
          ["paragraph", "heading", "list-item", "blockquote"].includes(
            node.type,
          )
        ) {
          parts.push(" ");
        }
      }
    };

    walk(root);

    return parts.join("").replaceAll(/\s+/g, " ").trim();
  };

  const toPlainText = (value) => {
    const raw = String(value ?? "");
    if (!raw) return "";

    // Storefront API returns Rich text fields as a JSON string.
    // Convert that into plain text, similar to Liquid's `metafield_text`.
    const trimmed = raw.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        const extracted = richTextJsonToPlainText(parsed);
        if (extracted) return extracted;
      } catch {
        // Fall through to HTML parsing.
      }
    }

    try {
      const doc = new DOMParser().parseFromString(raw, "text/html");
      return String(doc.body.textContent ?? "")
        .replaceAll(/\s+/g, " ")
        .trim();
    } catch {
      return raw
        .replaceAll(/<[^>]*>/g, " ")
        .replaceAll(/\s+/g, " ")
        .trim();
    }
  };

  const setLoadingState = (isLoading) => {
    grid.setAttribute("aria-busy", isLoading ? "true" : "false");

    if (loader) loader.hidden = !isLoading;
    if (errorMessage) errorMessage.hidden = true;
    if (emptyState) emptyState.hidden = true;
    if (loadMoreWrapper) loadMoreWrapper.hidden = true;
  };

  const showErrorState = () => {
    grid.setAttribute("aria-busy", "false");
    if (loader) loader.hidden = true;
    if (errorMessage) errorMessage.hidden = false;
    if (emptyState) emptyState.hidden = true;
    if (loadMoreWrapper) loadMoreWrapper.hidden = true;
  };

  const showEmptyState = () => {
    grid.setAttribute("aria-busy", "false");
    if (loader) loader.hidden = true;
    if (errorMessage) errorMessage.hidden = true;
    if (emptyState) emptyState.hidden = false;
    if (loadMoreWrapper) loadMoreWrapper.hidden = true;
  };

  const storefrontRequest = async (query, variables) => {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed (${response.status})`);
    }

    const payload = await response.json();

    if (payload.errors?.length) {
      const message = payload.errors.map((e) => e.message).join("; ");
      throw new Error(message);
    }

    return payload.data;
  };

  const getTodayIsoDate = () => {
    // ISO date in UTC (YYYY-MM-DD) is safe for lexical comparison.
    return new Date().toISOString().slice(0, 10);
  };

  const parseIsoDateKey = (value) => {
    const raw = String(value ?? "").trim();
    // Expected from Shopify Date field: YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    return "";
  };

  const formatDateForCard = (isoDate) => {
    // Render like Liquid: "%d %B %Y" | upcase.
    if (!isoDate) return "";

    const [y, m, d] = isoDate.split("-").map((n) => Number(n));
    if (!y || !m || !d) return "";

    // Use UTC to avoid off-by-one across timezones.
    const date = new Date(Date.UTC(y, m - 1, d));
    const locale = document.documentElement.lang || navigator.language || "en";

    try {
      const day = String(d).padStart(2, "0");
      const month = new Intl.DateTimeFormat(locale, {
        month: "long",
        timeZone: "UTC",
      }).format(date);
      const year = String(y);

      // Intl ordering is locale-dependent (e.g. en-US is Month Day Year).
      // We want consistent output matching Liquid: DD Month YYYY.
      return `${day} ${month} ${year}`.toUpperCase();
    } catch {
      return `${String(d).padStart(2, "0")} ${String(m).padStart(2, "0")} ${y}`;
    }
  };

  const fetchAllEvents = async () => {
    const query = `
      query Events($first: Int!, $after: String) {
        metaobjects(type: "event", first: $first, after: $after) {
          pageInfo { hasNextPage endCursor }
          edges {
            node {
              id
              handle
              title: field(key: "title") { value }
              date: field(key: "date") { value }
              summary: field(key: "summary") { value }
              featured_image: field(key: "featured_image") {
                reference {
                  ... on MediaImage {
                    presentation {
                      asJson(format: IMAGE)
                    }
                    image {
                      url(transform: { maxWidth: 800 })
                      altText
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const events = [];
    let after = null;

    for (;;) {
      const data = await storefrontRequest(query, { first: 100, after });
      const connection = data?.metaobjects;
      const edges = connection?.edges ?? [];

      for (const edge of edges) {
        const node = edge?.node;
        if (!node?.id || !node?.handle) continue;

        const isoDate = parseIsoDateKey(node?.date?.value);
        const image = node?.featured_image?.reference?.image;
        const presentation =
          node?.featured_image?.reference?.presentation?.asJson;

        events.push({
          id: node.id,
          handle: node.handle,
          title: node?.title?.value ?? "",
          isoDate,
          summary: node?.summary?.value ?? "",
          image: image
            ? {
                url: image.url,
                altText: image.altText ?? "",
                width: image.width ?? 800,
                height: image.height ?? 600,
                presentation,
              }
            : null,
        });
      }

      if (!connection?.pageInfo?.hasNextPage) break;
      after = connection.pageInfo.endCursor;
      if (!after) break;
    }

    return events;
  };

  const extractFocalPoint = (presentation) => {
    // MediaPresentation.asJson is a JSON scalar. Shape can evolve, so be defensive.
    if (!presentation) return null;

    let data = presentation;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        return null;
      }
    }

    const findPoint = (node) => {
      if (!node) return null;
      if (Array.isArray(node)) {
        for (const child of node) {
          const hit = findPoint(child);
          if (hit) return hit;
        }
        return null;
      }

      if (typeof node !== "object") return null;

      const candidate = node.focalPoint ?? node.focal_point ?? node.focal;
      if (candidate) {
        if (Array.isArray(candidate) && candidate.length >= 2) {
          return { x: candidate[0], y: candidate[1] };
        }
        if (typeof candidate === "object") {
          const x = candidate.x ?? candidate.X ?? candidate["x"];
          const y = candidate.y ?? candidate.Y ?? candidate["y"];
          if (x != null && y != null) return { x, y };
        }
      }

      // Common alternative keys.
      if (
        node.x != null &&
        node.y != null &&
        (node.type === "focalPoint" || node.kind === "focalPoint")
      ) {
        return { x: node.x, y: node.y };
      }

      for (const value of Object.values(node)) {
        const hit = findPoint(value);
        if (hit) return hit;
      }

      return null;
    };

    const rawPoint = findPoint(data);
    if (!rawPoint) return null;

    const toPercent = (value) => {
      const n = Number(value);
      if (!Number.isFinite(n)) return null;
      // Shopify commonly uses 0..1; tolerate 0..100.
      if (n >= 0 && n <= 1) return n * 100;
      if (n >= 0 && n <= 100) return n;
      return null;
    };

    const x = toPercent(rawPoint.x);
    const y = toPercent(rawPoint.y);
    if (x == null || y == null) return null;

    return {
      x: Math.min(100, Math.max(0, x)),
      y: Math.min(100, Math.max(0, y)),
    };
  };

  const renderCard = (event) => {
    const titleText = String(event.title ?? "").trim();
    const safeTitle = escapeHtml(titleText);
    const safeHandle = escapeHtml(event.handle);

    const href = `/pages/event/${safeHandle}`;

    const dateHtml = event.isoDate
      ? `
        <div class="events-page__card-date">
          <time datetime="${escapeHtml(event.isoDate)}">
            ${escapeHtml(formatDateForCard(event.isoDate))}
          </time>
        </div>
      `
      : "";

    const summary = toPlainText(event.summary);

    const summaryHtml = summary
      ? `
        <div class="events-page__card-summary">
          ${escapeHtml(summary)}
        </div>
      `
      : "";

    const focal = extractFocalPoint(event.image?.presentation);
    const objectPositionStyle = focal
      ? ` style="object-position: ${focal.x.toFixed(2)}% ${focal.y.toFixed(2)}%;"`
      : "";

    const imageHtml = event.image
      ? `
        <img
          src="${escapeHtml(event.image.url)}"
          alt="${safeTitle}"
          loading="lazy"
          width="${escapeHtml(event.image.width || 800)}"
          height="${escapeHtml(event.image.height || 600)}"
          ${objectPositionStyle}
        >
      `
      : `
        <div class="events-page__card-placeholder">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21,15 16,10 5,21"/>
          </svg>
        </div>
      `;

    return `
      <article class="events-page__card">
        <a href="${escapeHtml(href)}" class="events-page__card-link">
          <div class="events-page__card-image">
            ${imageHtml}
          </div>

          <div class="events-page__card-content">
            ${dateHtml}
            <h2 class="events-page__card-title">${safeTitle}</h2>
            ${summaryHtml}
          </div>
        </a>
      </article>
    `;
  };

  let allEvents = [];
  let renderedCount = 0;

  const renderNextPage = () => {
    const remaining = allEvents.length - renderedCount;
    if (remaining <= 0) {
      if (loadMoreWrapper) loadMoreWrapper.hidden = true;
      return;
    }

    const nextCount =
      pageSize === Infinity ? remaining : Math.min(remaining, pageSize);
    const slice = allEvents.slice(renderedCount, renderedCount + nextCount);
    const html = slice.map(renderCard).join("");

    if (loadMoreWrapper) {
      loadMoreWrapper.insertAdjacentHTML("beforebegin", html);
    } else {
      grid.insertAdjacentHTML("beforeend", html);
    }
    renderedCount += nextCount;

    if (loadMoreWrapper) {
      loadMoreWrapper.hidden = renderedCount >= allEvents.length;
    }
  };

  const main = async () => {
    setLoadingState(true);

    try {
      let events;
      try {
        events = await fetchAllEvents();
      } catch (error) {
        console.error(error);
        showErrorState();
        return;
      }

      const today = getTodayIsoDate();
      allEvents = events
        .filter((e) => {
          if (!e.isoDate) return true;
          if (showPastEvents) return true;
          return e.isoDate >= today;
        })
        .sort((a, b) => {
          // Most recent first. Missing dates go last.
          if (a.isoDate && b.isoDate) {
            if (a.isoDate > b.isoDate) return -1;
            if (a.isoDate < b.isoDate) return 1;
          } else if (a.isoDate && !b.isoDate) {
            return -1;
          } else if (!a.isoDate && b.isoDate) {
            return 1;
          }

          const aTitle = String(a.title ?? "").toLowerCase();
          const bTitle = String(b.title ?? "").toLowerCase();
          if (aTitle < bTitle) return -1;
          if (aTitle > bTitle) return 1;
          return 0;
        });

      // Remove placeholders once we start rendering.
      if (!allEvents.length) {
        showEmptyState();
        return;
      }

      // If we got here, the load succeeded, so ensure the error stays hidden.
      if (errorMessage) errorMessage.hidden = true;

      // Clear out loader/error/empty/load more placeholders before injecting cards.
      const placeholders = [loader, errorMessage, emptyState].filter(Boolean);
      for (const el of placeholders) el.remove();

      grid.setAttribute("aria-busy", "false");

      // Setup load more, then render first page.
      if (loadMoreWrapper) {
        loadMoreWrapper.hidden =
          pageSize === Infinity || allEvents.length <= pageSize;
      }

      renderNextPage();

      if (loadMoreButton) {
        loadMoreButton.addEventListener("click", () => {
          try {
            renderNextPage();
          } catch (error) {
            // If something goes wrong after content is already rendered,
            // don't replace the page with an error message.
            console.error(error);
          }
        });
      }
    } catch (error) {
      // Unexpected errors: only show the error UI if nothing rendered.
      console.error(error);
      if (renderedCount === 0) {
        showErrorState();
        return;
      }

      grid.setAttribute("aria-busy", "false");
      if (loader) loader.hidden = true;
    }
  };

  main();
})();
